import { evaluate, toast } from "@kyan/ui";
import { useReducer } from "react";
import { useDebounceValue } from "usehooks-ts";

import { tokenAddresses } from "#/config/web3/tokens";
import { useTransferButtonFeatureFlag } from "#/hooks/featureFlags/useTransferButtonFeatureFlag";
import { useChainId } from "#/hooks/useChainId";
import { usePortfolioToastAction } from "#/hooks/usePortfolioToastActions";
import { useRefetchAccountStateIfStale } from "#/hooks/useRefetchAccountStateIfStale";
import { useAccountHistory } from "#/services/kyan/hooks/useAccountHistory";
import { useTransfer } from "#/services/kyan/hooks/useTransfer";
import { useIsEquityModalEditing } from "#/store/app/equityModal/equityModalEditState";
import { useEquityModalPortfolio } from "#/store/app/equityModal/equityModalPortfolio";
import { useAccount } from "#/store/wallet/account/account";
import { useAccountState } from "#/store/wallet/account/accountState";
import { type PortfolioSymbolType } from "#/types/markets";

import { useTxState } from "../../../../EquityModal.hook";
import { TxStatus } from "../../../../EquityModal.types";
import {
  initialTransferFormState,
  transferFormReducer,
} from "./TransferForm.utils";

export const useTransferFormController = () => {
  const { chainId } = useChainId();
  const { setTxState } = useTxState();
  const { account, setAccount } = useAccount();
  const { setIsEquityModalEditing } = useIsEquityModalEditing();
  const { refetch: refetchAccountHistory } = useAccountHistory();
  const { refetch } = useRefetchAccountStateIfStale();
  const { getAction } = usePortfolioToastAction();

  const [formState, dispatch] = useReducer(
    transferFormReducer,
    initialTransferFormState,
  );
  const {
    equityModalPortfolio: toPortfolio,
    setEquityModalPortfolio: setToPortfolio,
  } = useEquityModalPortfolio();
  const { amount, fromPortfolio } = formState;
  const [debouncedAmount] = useDebounceValue(amount, 600);

  const { portfolioState: fromPortfolioState } = useAccountState(fromPortfolio);
  const { portfolioState: toPortfolioState } = useAccountState(toPortfolio);

  const {
    isTransferButtonEnabled,
    transferButtonTitle,
    transferButtonTooltip,
  } = useTransferButtonFeatureFlag();

  const isAmountValid = !isNaN(Number(amount)) && Number(amount) > 0;

  const ctaLabel =
    evaluate({
      "Enter amount": !isAmountValid,
    }) ?? transferButtonTitle;

  const errorMessage = evaluate({
    "Insufficient equity": Number(debouncedAmount) > fromPortfolioState.equity,
  });

  const isDisabled =
    !isAmountValid ||
    !isTransferButtonEnabled ||
    fromPortfolio === toPortfolio ||
    Boolean(errorMessage);

  const { mutate: transferMutation, isPending: isLoading } = useTransfer({
    onSuccess: () => {
      toast.success({
        title: "Transfer successful",
        action: getAction({
          closeModal: true,
          target: "history",
          label: "View in history",
        }),
      });
      setTxState({
        status: TxStatus.SUCCESS,
        amount: Number(amount),
        fromPortfolio,
        toPortfolio,
      });
      refetchAccountHistory();
      refetch?.();
    },
    onError: (error) => {
      toast.error({ title: "Transfer failed", description: error.message });
      setTxState({ status: TxStatus.ERROR });
    },
    onPendingTransfer: (signedTransfer) => {
      setTxState({ status: TxStatus.PENDING, amount: signedTransfer.amount });
    },
  });

  /// @dev testing EoA accounts only for now:
  const handleTransfer = () => {
    if (!account) return;

    const transferAmount = Number(amount);

    const fromPair = {
      base: tokenAddresses[fromPortfolio][chainId],
      quote: tokenAddresses.USDC[chainId],
    };
    const toPair = {
      base: tokenAddresses[toPortfolio][chainId],
      quote: tokenAddresses.USDC[chainId],
    };

    transferMutation({
      account,
      amount: transferAmount,
      from_pair: fromPair,
      to_pair: toPair,
    });
  };

  const setTransferAmount = (transferAmount: string) => {
    dispatch({ type: "SET_AMOUNT", payload: transferAmount });
    setIsEquityModalEditing(Boolean(transferAmount));
  };

  return {
    handleTransfer,
    ctaLabel,
    tooltip: transferButtonTooltip,
    isDisabled,
    isAmountValid,
    isLoading,
    amount,
    setTransferAmount,
    fromPortfolio,
    setFromPortfolio: (market: PortfolioSymbolType) => {
      dispatch({ type: "SET_FROM_PORTFOLIO", payload: market });
    },
    toPortfolio,
    setToPortfolio,
    fromPortfolioState,
    toPortfolioState,
    account,
    setAccount,
    errorMessage,
    finalFromAmount: (fromPortfolioState.equity - Number(amount)).toString(),
    finalToAmount: (toPortfolioState.equity + Number(amount)).toString(),
  };
};
